import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { Tooltip } from 'react-tooltip';

import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { convertTimeToMMSS } from '@src/utility/timeFormat';

import { getBeatKey, getStoryThumbnailUrl, resolveVideoSrc } from './clipSequenceUtils';
import { useReelBeatReorder } from './useReelBeatReorder';

import type { ReelOutline } from './types';
import type { ReelClipResults } from '@src/services/reelClipProducer/types';

// --- ClipMiniPlayer ---

type ClipMiniPlayerProps = {
  videoSrc: string;
  thumbnailUrl: string;
  startTime: number;
  duration: number;
};

const ClipMiniPlayer: React.FC<ClipMiniPlayerProps> = ({
  videoSrc,
  thumbnailUrl,
  startTime,
  duration,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const rafRef = useRef<number | null>(null);

  const clipEnd = startTime + duration;

  const stopPlayback = useCallback(() => {
    const video = videoRef.current;
    if (video) video.pause();
    setIsPlaying(false);
    setIsLoading(false);
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  const handleClick = useCallback(() => {
    const video = videoRef.current;
    if (!video || !videoSrc) return;

    if (isPlaying) {
      stopPlayback();
      return;
    }

    if (!isLoaded) {
      setIsLoading(true);
      video.src = videoSrc;
      video.load();

      const onCanPlay = () => {
        video.removeEventListener('canplay', onCanPlay);
        setIsLoaded(true);
        setIsLoading(false);
        video.currentTime = startTime;

        const seeked = () => {
          video.removeEventListener('seeked', seeked);
          video.play()
            .then(() => setIsPlaying(true))
            .catch(() => setIsPlaying(false));
        };
        video.addEventListener('seeked', seeked);
      };
      video.addEventListener('canplay', onCanPlay);
      return;
    }

    video.currentTime = startTime;
    const seeked = () => {
      video.removeEventListener('seeked', seeked);
      video.play()
        .then(() => setIsPlaying(true))
        .catch(() => setIsPlaying(false));
    };
    video.addEventListener('seeked', seeked);
  }, [isPlaying, isLoaded, videoSrc, startTime, stopPlayback]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !isPlaying) return;

    const checkTime = () => {
      if (video.currentTime >= clipEnd - 0.05) {
        stopPlayback();
        return;
      }
      rafRef.current = requestAnimationFrame(checkTime);
    };

    rafRef.current = requestAnimationFrame(checkTime);
    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [isPlaying, clipEnd, stopPlayback]);

  if (!videoSrc) {
    return (
      <PlayerArea>
        {thumbnailUrl ? (
          <PlayerThumbnail src={thumbnailUrl} alt="clip" loading="lazy" />
        ) : (
          <PlayerPlaceholder>No preview</PlayerPlaceholder>
        )}
      </PlayerArea>
    );
  }

  return (
    <PlayerArea onClick={handleClick}>
      <PlayerVideo
        ref={videoRef}
        preload="none"
        $visible={isPlaying}
      />
      {!isPlaying && thumbnailUrl && (
        <PlayerThumbnail src={thumbnailUrl} alt="clip" loading="lazy" />
      )}
      {isLoading && (
        <PlayOverlay $visible>
          <PlayIcon>&#8987;</PlayIcon>
        </PlayOverlay>
      )}
      {!isLoading && (
        <PlayOverlay $visible={!isPlaying}>
          <PlayIcon>&#9654;</PlayIcon>
        </PlayOverlay>
      )}
    </PlayerArea>
  );
};

// --- ClipSequenceList ---

type ClipSequenceListProps = {
  outline: ReelOutline;
  clipResults: ReelClipResults;
  onOutlineChange: (outline: ReelOutline) => void;
  excludedBeats: Set<string>;
  onToggleBeat: (beatKey: string) => void;
};

const ClipSequenceList: React.FC<ClipSequenceListProps> = ({
  outline,
  clipResults,
  onOutlineChange,
  excludedBeats,
  onToggleBeat,
}) => {
  const videoCreator = useVideoCreatorStore();

  const videoSrc = useMemo(() => resolveVideoSrc(videoCreator.story), [videoCreator.story]);

  const getThumbnailUrl = useCallback(
    (timecode?: number): string => getStoryThumbnailUrl(videoCreator.story, timecode),
    [videoCreator.story],
  );

  const {
    chapterDragKey,
    chapterDragOverKey,
    handleChapterDragStart,
    handleChapterDragOver,
    handleChapterDragLeave,
    handleChapterDrop,
    handleChapterDragEnd,
    beatDragKey,
    beatDragOverKey,
    handleBeatDragStart,
    handleBeatDragOver,
    handleBeatDragLeave,
    handleBeatDrop,
    handleBeatDragEnd,
  } = useReelBeatReorder(outline, onOutlineChange);

  const beatEstimatedOffsets = useMemo(() => {
    const offsets = new Map<string, number>();
    let cumulative = 0;
    for (const chapter of outline.chapters) {
      for (const beat of chapter.beats) {
        offsets.set(getBeatKey(chapter, beat), cumulative);
        cumulative += beat.estimatedDuration;
      }
    }
    return offsets;
  }, [outline]);

  const hasBeats = outline.chapters.some(ch => ch.beats.length > 0);

  if (outline.chapters.length === 0 || !hasBeats) {
    return (
      <PanelContainer>
        <EmptyState>No clips to display.</EmptyState>
      </PanelContainer>
    );
  }

  return (
    <PanelContainer>
      <ScrollableArea>
        {outline.chapters.map((chapter, chapterIdx) => {
          const chapterDuration = chapter.beats.reduce(
            (sum, beat) => sum + beat.estimatedDuration,
            0,
          );
          const chapterKey = String(chapterIdx);
          const isChapterDragging = chapterDragKey === chapterKey;
          const isChapterDragOver = chapterDragOverKey === chapterKey;

          return (
            <ChapterContainer
              key={`ch-${chapterIdx}`}
              $isDragging={isChapterDragging}
              $isDragOver={isChapterDragOver}
              onDragOver={e => handleChapterDragOver(e, chapterIdx)}
              onDragLeave={handleChapterDragLeave}
              onDrop={e => handleChapterDrop(e, chapterIdx)}
            >
              <ChapterHeader>
                <ChapterDragHandle
                  draggable
                  onDragStart={e => handleChapterDragStart(e, chapterIdx)}
                  onDragEnd={handleChapterDragEnd}
                >
                  <DragDots>⠿</DragDots>
                </ChapterDragHandle>
                <ChapterTime>{convertTimeToMMSS(chapterDuration)}</ChapterTime>
                <ChapterTitle>
                  Ch {chapterIdx + 1}: {chapter.title}
                </ChapterTitle>
              </ChapterHeader>
              <ClipsContainer>
                {chapter.beats.map((beat, beatIdx) => {
                  const key = getBeatKey(chapter, beat);
                  const result = clipResults.get(key);
                  const selectedOption = result
                    ? result.options[result.selectedOptionIndex]
                    : null;

                  const clipTitle =
                    selectedOption?.title || beat.shortTitle || beat.description;
                  const clipText = selectedOption?.text || beat.description;
                  const duration =
                    selectedOption?.clipFragment?.duration ?? beat.estimatedDuration;
                  const resolvedStartTime = selectedOption?.clipFragment?.startTime ?? null;
                  const score = selectedOption?.overallScore ?? null;
                  const speakerName =
                    selectedOption?.clipFragment?.speakerName ||
                    beat.speaker ||
                    null;

                  const scores = selectedOption?.scores ?? null;
                  const justification = selectedOption?.justification ?? null;
                  const hasFragment = !!selectedOption?.clipFragment;
                  const optionCount = result?.options.length ?? 0;
                  const rejectReason = selectedOption?.rejectReason ?? null;

                  const isExcluded = excludedBeats.has(key);
                  const thumbTimecode =
                    resolvedStartTime ?? beatEstimatedOffsets.get(key) ?? 0;
                  const thumbnailUrl = getThumbnailUrl(thumbTimecode);
                  const durationFormatted = convertTimeToMMSS(Math.round(duration));

                  const beatDragId = `${chapterIdx}.${beatIdx}`;
                  const isBeatDragging = beatDragKey === beatDragId;
                  const isBeatDragOver = beatDragOverKey === beatDragId;

                  return (
                    <ClipCard
                      key={key}
                      $excluded={isExcluded}
                      $isDragging={isBeatDragging}
                      $isDragOver={isBeatDragOver}
                      onDragOver={e => handleBeatDragOver(e, chapterIdx, beatIdx)}
                      onDragLeave={handleBeatDragLeave}
                      onDrop={e => handleBeatDrop(e, chapterIdx, beatIdx)}
                    >
                      <CardActions>
                        <ToggleButton
                          $included={!isExcluded}
                          onClick={() => onToggleBeat(key)}
                          aria-label={isExcluded ? 'Include clip' : 'Exclude clip'}
                        >
                          {isExcluded ? '' : '✓'}
                        </ToggleButton>
                        {!isExcluded && (
                          <RemoveButton
                            onClick={() => onToggleBeat(key)}
                            aria-label="Remove clip"
                          >
                            &times;
                          </RemoveButton>
                        )}
                      </CardActions>
                      <CardBody>
                        <BeatDragHandle
                          draggable
                          onDragStart={e => handleBeatDragStart(e, chapterIdx, beatIdx)}
                          onDragEnd={handleBeatDragEnd}
                        >
                          <DragDots>⠿</DragDots>
                        </BeatDragHandle>
                        {hasFragment ? (
                          <ClipMiniPlayer
                            videoSrc={videoSrc}
                            thumbnailUrl={thumbnailUrl}
                            startTime={thumbTimecode}
                            duration={duration}
                          />
                        ) : (
                          <PlayerArea>
                            <GeneratingPlaceholder>Generating clips...</GeneratingPlaceholder>
                          </PlayerArea>
                        )}
                        <CardContent>
                          <BadgeRow>
                            <DurationBadge>{durationFormatted}</DurationBadge>
                            {score !== null && score > 0 && (
                              <ScoreGroup>
                                <ScoreBadge>{score}</ScoreBadge>
                                {scores && (
                                  <ScoreInfoTrigger>
                                    <ScoreInfoIcon>&#9432;</ScoreInfoIcon>
                                    <ScoreTooltip>
                                      <ScoreTooltipTitle>Impact Score {score}</ScoreTooltipTitle>
                                      <ScoreTooltipRow><span>Relevance</span><ScoreTooltipValue>{scores.relevance}</ScoreTooltipValue></ScoreTooltipRow>
                                      <ScoreTooltipRow><span>Engagement</span><ScoreTooltipValue>{scores.narrativeFlow}</ScoreTooltipValue></ScoreTooltipRow>
                                      <ScoreTooltipRow><span>Uniqueness</span><ScoreTooltipValue>{scores.completeness}</ScoreTooltipValue></ScoreTooltipRow>
                                      <ScoreTooltipRow><span>Audience</span><ScoreTooltipValue>{scores.speakerMatch}</ScoreTooltipValue></ScoreTooltipRow>
                                      {justification && (
                                        <ScoreTooltipJustification>{justification}</ScoreTooltipJustification>
                                      )}
                                    </ScoreTooltip>
                                  </ScoreInfoTrigger>
                                )}
                              </ScoreGroup>
                            )}
                          </BadgeRow>
                          {clipTitle && <ClipTitleText>{clipTitle}</ClipTitleText>}
                          <ClipTranscript
                            data-tooltip-id="clip-tooltip"
                            data-tooltip-content={clipText}
                            data-tooltip-delay-show={150}
                          >
                            {clipText}
                          </ClipTranscript>
                          <ClipBottomRow>
                            <ClipTimeRange>
                              {convertTimeToMMSS(Math.round(thumbTimecode))} - {convertTimeToMMSS(Math.round(thumbTimecode + duration))}
                            </ClipTimeRange>
                            {speakerName && <ClipSpeaker>{speakerName}</ClipSpeaker>}
                          </ClipBottomRow>
                          {process.env.NODE_ENV === 'development' && (
                            <DebugRow>
                              <DebugLabel $ok={hasFragment}>
                                {hasFragment ? 'CLIP RESOLVED' : 'NO CLIP FRAGMENT'}
                              </DebugLabel>
                              <DebugLabel $ok={optionCount > 0}>
                                {optionCount} opts
                              </DebugLabel>
                              <DebugLabel $ok={resolvedStartTime !== null}>
                                {resolvedStartTime !== null
                                  ? `${convertTimeToMMSS(Math.round(resolvedStartTime))} – ${convertTimeToMMSS(Math.round(resolvedStartTime + duration))}`
                                  : 'no time'}
                              </DebugLabel>
                              {rejectReason && (
                                <DebugLabel $ok={false}>{rejectReason}</DebugLabel>
                              )}
                            </DebugRow>
                          )}
                        </CardContent>
                      </CardBody>
                    </ClipCard>
                  );
                })}
              </ClipsContainer>
            </ChapterContainer>
          );
        })}
      </ScrollableArea>
      <Tooltip
        id="clip-tooltip"
        place="bottom"
        style={{
          maxWidth: '400px',
          fontSize: '12px',
          lineHeight: '1.5',
          zIndex: 100001,
          borderRadius: '6px',
          padding: '8px 12px',
        }}
      />
    </PanelContainer>
  );
};

ClipSequenceList.displayName = 'ClipSequenceList';

export default ClipSequenceList;

// --- Styled Components ---

const PanelContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: var(--background);
  padding: 12px 16px;
`;

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  font-size: 13px;
  color: var(--text-muted);
`;

const ScrollableArea = styled.div`
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 10px;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const ChapterContainer = styled.div<{ $isDragging?: boolean; $isDragOver?: boolean }>`
  display: flex;
  flex-direction: column;
  opacity: ${p => (p.$isDragging ? 0.4 : 1)};
  transition: opacity 150ms ease;
  border-top: ${p => (p.$isDragOver ? '2px solid var(--primary)' : '2px solid transparent')};
`;

const ChapterHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
  margin-bottom: 4px;
`;

const DragDots = styled.span`
  font-size: 16px;
  line-height: 1;
  user-select: none;
`;

const ChapterDragHandle = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  color: var(--text-muted);
  opacity: 0.4;
  cursor: grab;
  transition: opacity 150ms ease;
  padding: 2px;

  &:hover {
    opacity: 1;
  }

  &:active {
    cursor: grabbing;
  }
`;

const BeatDragHandle = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  color: var(--text-muted);
  opacity: 0.3;
  cursor: grab;
  transition: opacity 150ms ease;
  align-self: center;

  &:hover {
    opacity: 1;
  }

  &:active {
    cursor: grabbing;
  }
`;

const ChapterTime = styled.span`
  font-size: 11px;
  color: var(--text-muted);
  font-variant-numeric: tabular-nums;
  flex-shrink: 0;
`;

const ChapterTitle = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: var(--foreground);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ClipsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ClipCard = styled.div<{ $excluded: boolean; $isDragging?: boolean; $isDragOver?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 16px;
  border-radius: 8px;
  background-color: var(--surface, rgba(255, 255, 255, 0.04));
  color: var(--text-secondary);
  transition: box-shadow 150ms ease, opacity 150ms ease;
  opacity: ${p => (p.$isDragging ? 0.4 : p.$excluded ? 0.5 : 1)};
  border-top: ${p => (p.$isDragOver ? '2px solid var(--primary)' : '2px solid transparent')};

  &:hover {
    box-shadow: var(--elevation-sm, 0 2px 8px rgba(0, 0, 0, 0.2));
  }
`;

const CardActions = styled.div`
  position: absolute;
  right: 12px;
  top: 12px;
  display: flex;
  align-items: center;
  gap: 6px;
  z-index: 1;
`;

const ToggleButton = styled.button<{ $included: boolean }>`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 1.5px solid var(--text-muted);
  background: transparent;
  color: var(--text-muted);
  font-size: 10px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  transition: all 150ms ease;

  ${props =>
    props.$included &&
    `
    background-color: var(--primary);
    border-color: var(--primary);
    color: var(--background);
  `}
`;

const RemoveButton = styled.button`
  width: 16px;
  height: 16px;
  border-radius: 4px;
  border: none;
  background: transparent;
  color: var(--text-muted);
  font-size: 14px;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  transition: all 150ms ease;

  &:hover {
    color: var(--toast-error, #e53e3e);
  }
`;

const BadgeRow = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
  margin-bottom: 4px;
`;

const DurationBadge = styled.div`
  padding: 2px 5px;
  background-color: var(--toast-error, #e53e3e);
  color: black;
  width: fit-content;
  border-radius: 2px;
  font-size: var(--text-xs, 11px);
  font-weight: var(--font-weight-medium, 500);
`;

const ScoreGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ScoreBadge = styled.span`
  font-size: 20px;
  font-weight: 700;
  color: #4ade80;
  line-height: 1;
`;

const ScoreInfoTrigger = styled.div`
  position: relative;
  display: flex;
  align-items: center;

  &:hover > div {
    display: flex;
  }
`;

const ScoreInfoIcon = styled.span`
  font-size: 14px;
  color: var(--text-muted);
  cursor: pointer;
  opacity: 0.6;
  transition: opacity 150ms ease;

  ${ScoreInfoTrigger}:hover & {
    opacity: 1;
  }
`;

const ScoreTooltip = styled.div`
  display: none;
  flex-direction: column;
  gap: 6px;
  position: absolute;
  top: calc(100% + 6px);
  left: 50%;
  transform: translateX(-50%);
  background: var(--surface-elevated, #1a1a1a);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 12px 16px;
  min-width: 180px;
  z-index: 100001;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
`;

const ScoreTooltipTitle = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: var(--foreground);
  margin-bottom: 2px;
`;

const ScoreTooltipRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 11px;
  color: var(--text-secondary);
`;

const ScoreTooltipValue = styled.span`
  font-weight: 600;
  color: #4ade80;
  font-variant-numeric: tabular-nums;
`;

const ScoreTooltipJustification = styled.div`
  font-size: 10px;
  font-style: italic;
  color: var(--text-muted);
  line-height: 1.4;
  margin-top: 4px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  padding-top: 6px;
`;

// --- ClipMiniPlayer Styled ---

const CardBody = styled.div`
  display: flex;
  gap: 12px;
`;

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  justify-content: center;
`;

const PlayerArea = styled.div`
  position: relative;
  flex-shrink: 0;
  width: 240px;
  height: 135px;
  border-radius: 6px;
  overflow: hidden;
  background: #000;
  cursor: pointer;
`;

const PlayerVideo = styled.video<{ $visible: boolean }>`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: ${props => (props.$visible ? 'block' : 'none')};
`;

const PlayerThumbnail = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const PlayerPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  color: var(--text-muted);
`;

const GeneratingPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: var(--text-muted);
  background: rgba(255, 255, 255, 0.03);
`;

const PlayOverlay = styled.div<{ $visible: boolean }>`
  position: absolute;
  inset: 0;
  display: ${props => (props.$visible ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.35);
  transition: opacity 150ms ease;
`;

const PlayIcon = styled.span`
  color: #fff;
  font-size: 28px;
  filter: drop-shadow(0 1px 3px rgba(0, 0, 0, 0.5));
`;

// --- Card Content Styled ---

const ClipTitleText = styled.div`
  color: var(--foreground);
  font-size: 12px;
  font-weight: 600;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const ClipSpeaker = styled.span`
  font-size: 10px;
  font-weight: var(--font-weight-medium, 500);
  color: var(--accent);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 50%;
`;

const DebugRow = styled.div`
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
  margin-top: 4px;
`;

const DebugLabel = styled.span<{ $ok: boolean }>`
  font-size: 9px;
  font-family: monospace;
  padding: 1px 4px;
  border-radius: 2px;
  background: ${props => (props.$ok ? 'rgba(0, 200, 0, 0.15)' : 'rgba(255, 60, 60, 0.15)')};
  color: ${props => (props.$ok ? '#4ade80' : '#f87171')};
`;

const ClipTranscript = styled.div`
  color: var(--text-secondary);
  font-size: 10px;
  font-weight: var(--font-weight-normal, 400);
  line-height: 140%;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  margin: 2px 0 4px 0;
  cursor: default;
`;

const ClipBottomRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const ClipTimeRange = styled.span`
  color: var(--text-muted);
  font-size: 10px;
  font-weight: var(--font-weight-medium, 500);
  font-variant-numeric: tabular-nums;
  line-height: normal;
`;

